import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { BankLogo } from './BankLogo.js';
import type { BankMaster } from '@/features/finance/api.js';

interface BankSelectProps {
  banks: BankMaster[];
  value: string;
  onChange: (code: string) => void;
  onBlur?: () => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder: string;
  loadingLabel: string;
}

export function BankSelect({
  banks,
  value,
  onChange,
  onBlur,
  loading,
  disabled,
  placeholder,
  loadingLabel,
}: BankSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const selected = banks.find((b) => b.code === value);
  const isDisabled = disabled || loading;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        onBlur?.();
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, onBlur]);

  const fullName = (b: BankMaster) => (b.localName ? `${b.name} · ${b.localName}` : b.name);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 h-11 px-3 rounded-lg border border-(--border) bg-(--bg-elevated) text-[14px] font-semibold text-(--text-primary) outline-none focus:border-(--primary) disabled:opacity-60"
      >
        {selected ? (
          <>
            <BankLogo code={selected.code} size={24} />
            <span className="flex-1 truncate text-left">{fullName(selected)}</span>
          </>
        ) : (
          <span className="flex-1 truncate text-left text-(--text-muted)">
            {loading ? loadingLabel : placeholder}
          </span>
        )}
        <ChevronDown size={16} className="shrink-0 text-(--text-muted)" />
      </button>
      {open && !isDisabled && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 mt-1.5 max-h-64 overflow-auto rounded-lg border border-(--border) bg-(--bg-raised) shadow-xl py-1 z-30"
        >
          {banks.map((b) => {
            const active = b.code === value;
            return (
              <li key={b.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(b.code);
                    setOpen(false);
                    onBlur?.();
                  }}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-(--bg-elevated) ${active ? 'bg-(--bg-elevated)' : ''}`}
                >
                  <BankLogo code={b.code} size={24} />
                  <span className="flex min-w-0 flex-col">
                    <span className={`truncate text-[13px] font-semibold ${active ? 'text-(--primary)' : 'text-(--text-primary)'}`}>
                      {b.name}
                    </span>
                    {b.localName && (
                      <span className="truncate text-[11px] text-(--text-muted)">{b.localName}</span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
